import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import * as ProcedureRunDAO from 'procedures/network/ProcedureRunDAO';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {SettingsLabel} from 'ui-library/SettingsField';

interface ProcedureRunDescriptionBaseEditorProps {
  procedureRunId?: string;
  description: string;
  refetch?: () => void;
}

export type ProcedureRunDescriptionEditorProps = ProcedureRunDescriptionBaseEditorProps &
  (
    | {
        isBeingCreated: true;
        setDescription: (description: string) => void;
      }
    | {
        isBeingCreated: false;
        setDescription?: never;
      }
  );

export default function ProcedureRunDescriptionEditor({
  procedureRunId,
  description,
  refetch,
  isBeingCreated,
  setDescription,
}: ProcedureRunDescriptionEditorProps): JSX.Element | null {
  const handleSetDescription = isBeingCreated
    ? setDescription
    : async (description: string): Promise<void> => {
        assert(procedureRunId !== undefined, 'procedureRunId is required when updating description.');
        assert(refetch !== undefined, 'refetch is required when updating description.');
        await ProcedureRunDAO.updateProcedureRun(
          {
            id: procedureRunId,
            description,
          },
          {supportsAsync: false}
        );
        refetch();
      };
  return (
    <FormGroup>
      <div
        className={css(
          isBeingCreated ? styles.descriptionFormInputStyle : styles.descriptionEdibleTextFieldTitle
        )}
      >
        <SettingsLabel isRequired={false} label="Description" name="description" withBottomMargin={false} />
      </div>
      {isBeingCreated ? (
        <FormInput
          disabled={false}
          onChange={(e) => {
            handleSetDescription(e.target.value);
          }}
          id="description"
          type={TextFieldType.TEXTAREA}
          value={description}
          maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
        />
      ) : (
        <EditableTextField
          isDisabled={false}
          requestChange={(updatedDescription) => {
            handleSetDescription(updatedDescription as string);
          }}
          emptyValueAllowed={true}
          value={description}
          maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
          type={TextFieldType.TEXTAREA}
          viewerClassName={css(styles.textValueBlock)}
        />
      )}
    </FormGroup>
  );
}

const styles = StyleSheet.create({
  descriptionEdibleTextFieldTitle: {
    marginLeft: -10,
  },
  descriptionFormInputStyle: {
    marginBottom: 5,
  },
  textValueBlock: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
});
